import React, { useEffect, useRef, useState } from "react";
import { BsOpenai } from "react-icons/bs";
import { useLanguage } from "../../Translations/LenguageContext";
import { useNavigate } from "react-router-dom";
import Spinner from "../../Components/Spinner";
import ErrorMessage from "../../Components/ErrorMessage";
import EngineLoader from "../../Loaders/EngineLoader";
import { TbCoins } from "react-icons/tb";
import NewCardChargeModal from "../../Billing/Pages/NewCardChargeModal";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;
const STRIPE_PUBLISHABLE_KEY_TEST = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY_TEST);

const engines = [
    {
        id: "chatgpt",
        name: "ChatGPT",
    },
    {
        id: "claude",
        name: "Claude",
    },
    {
        id: "gemini",
        name: "Gemini",
    },
    {
        id: "perplexity",
        name: "Perplexity",
    },
];

const ENGINE_COST_PER_PROMPT = {
    chatgpt: 0.05,
    gemini: 0.05,
    claude: 0.05,
    perplexity: 0.05
};

const CREDIT_BALANCE = 5;

const MIN_COUNT = 10;
const MAX_COUNT = 250;
const COUNT_STEP = 10;

const GeoEngines = ({
    account_id,
    org_id,
    geo_id,
    selectedEngines,
    setSelectedEngines,
    count,
    setCount,
    saveEnginesGeo,
    setLoadingSave,
    loadingSave,
    errorMessage,
    setErrorMessage,
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate()
    const [isDragging, setIsDragging] = useState(false);
    const sliderRef = useRef(null);
    const [loadingEngines, setLoadingEngines] = useState(false)
    const [openCreditsModal, setOpenCreditsModal] = useState(false)

    const navigateBack = () => {
        setLoadingSave(false)
        setErrorMessage({ message: "", display: false });
        navigate(`/console/organizations/${org_id}/geo/${geo_id}/topics`);
    };

    const getEngines = async () => {
        setLoadingEngines(true);

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_engines`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setSelectedEngines(result.engines)
            result.simulation_count == 0 ? setCount(10) : setCount(result.simulation_count)

        } catch (err) {
            console.error("Error fetching GEO optimization:", err);
        } finally {
            setTimeout(() => {
                setLoadingEngines(false);
            }, 500);
        }
    };

    const toggleEngine = (engineId) => {
        setSelectedEngines((prev) =>
            prev.includes(engineId)
                ? prev.filter((id) => id !== engineId)
                : [...prev, engineId]
        );
    };

    const decreaseCount = () => {
        setCount((prev) =>
            Math.max(MIN_COUNT, prev - COUNT_STEP)
        );
    };

    const increaseCount = () => {
        setCount((prev) =>
            Math.min(MAX_COUNT, Number(prev) + COUNT_STEP)
        );
    };

    const updateCountFromPosition = (clientX) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();

        let percentage =
            (clientX - rect.left) / rect.width;

        percentage = Math.max(
            0,
            Math.min(1, percentage)
        );

        const rawCount =
            MIN_COUNT +
            percentage * (MAX_COUNT - MIN_COUNT);

        const steppedCount =
            Math.round(
                (rawCount - MIN_COUNT) / COUNT_STEP
            ) * COUNT_STEP +
            MIN_COUNT;

        const finalCount = Math.max(
            MIN_COUNT,
            Math.min(MAX_COUNT, steppedCount)
        );

        setCount(finalCount);
    };

    const handleMouseDown = (event) => {
        setIsDragging(true);
        updateCountFromPosition(event.clientX);
    };

    const creditBalance = CREDIT_BALANCE;

    const calculatedCost = selectedEngines.reduce(
        (total, engineId) => {
            return total + (ENGINE_COST_PER_PROMPT[engineId] || 0);
        },
        0
    ) * Number(count);

    const exceedsCreditBalance = calculatedCost > creditBalance;
    // const canProceed =
    //     selectedEngines.length > 0 && !exceedsCreditBalance;

    const canProceed =
        selectedEngines.length > 0

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (event) => {
            updateCountFromPosition(event.clientX);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        document.addEventListener(
            "mousemove",
            handleMouseMove
        );

        document.addEventListener(
            "mouseup",
            handleMouseUp
        );

        return () => {
            document.removeEventListener(
                "mousemove",
                handleMouseMove
            );

            document.removeEventListener(
                "mouseup",
                handleMouseUp
            );
        };
    }, [isDragging]);

    const handleTouchStart = (event) => {
        setIsDragging(true);

        updateCountFromPosition(
            event.touches[0].clientX
        );
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleTouchMove = (event) => {
            updateCountFromPosition(
                event.touches[0].clientX
            );
        };

        const handleTouchEnd = () => {
            setIsDragging(false);
        };

        document.addEventListener(
            "touchmove",
            handleTouchMove,
            { passive: false }
        );

        document.addEventListener(
            "touchend",
            handleTouchEnd
        );

        return () => {
            document.removeEventListener(
                "touchmove",
                handleTouchMove
            );

            document.removeEventListener(
                "touchend",
                handleTouchEnd
            );
        };
    }, [isDragging]);

    const sliderPercentage =
        ((count - MIN_COUNT) /
            (MAX_COUNT - MIN_COUNT)) *
        100;

    const progressPercentage = Math.max(3, sliderPercentage);

    useEffect(() => {
        getEngines();
    }, []);

    return (
        <div className="open_frame_body_inner">

            <p className="add_new_source_cont_title fade-in">
                {t.geo.geo_engine_title}
            </p>

            <p className="add_new_source_cont_desc fade-in">
                {t.geo.geo_engine_description}
            </p>

            {loadingEngines ? (
                <EngineLoader
                    engineCount={engines.length}
                />
            ) : (
                <>
                    <div className="engine_selection_cont">
                        {engines.map((engine) => {

                            const isSelected =
                                selectedEngines.includes(
                                    engine.id
                                );

                            return (
                                <button
                                    key={engine.id}
                                    type="button"
                                    className={`engine_option fade-in ${isSelected
                                        ? "engine_option_selected"
                                        : ""
                                        }`}
                                    onClick={() =>
                                        toggleEngine(
                                            engine.id
                                        )
                                    }
                                >
                                    <div
                                        className="engine_option_icon"
                                        style={
                                            engine.id !== "chatgpt"
                                                ? { backgroundImage: `url('/logos/${engine.id}.png')` }
                                                : undefined
                                        }
                                    >
                                        {engine.id === "chatgpt" && <span><BsOpenai /></span>}
                                    </div>
                                    <p className="engine_option_title">
                                        {engine.name}
                                    </p>
                                </button>
                            );
                        })}
                    </div>

                    <div className="simulations_counter_cont">

                        <button
                            type="button"
                            className="simulations_count_btn"
                            onClick={decreaseCount}
                            disabled={count <= MIN_COUNT}
                        >
                            <i className="fa-solid fa-minus"></i>
                        </button>

                        <div
                            ref={sliderRef}
                            className="simulations_increaser"
                            onMouseDown={handleMouseDown}
                            onTouchStart={handleTouchStart}
                        >
                            <div
                                className="simulations_increaser_progress"
                                style={{
                                    width: `${progressPercentage}%`,
                                }}
                            />

                            <div
                                className={`draggable_simulations_increaser ${isDragging ? "dragging" : ""
                                    }`}
                                style={{
                                    left: `${progressPercentage}%`,
                                }}
                            />
                        </div>

                        <button
                            type="button"
                            className="simulations_count_btn"
                            onClick={increaseCount}
                            disabled={count >= MAX_COUNT}
                        >
                            <i className="fa-solid fa-plus"></i>
                        </button>

                    </div>

                    <p className="warning_source_message fade-in">
                        {t.geo.generate} <strong className="simulations_count">{count}</strong> <span dangerouslySetInnerHTML={{ __html: t.geo.geo_prompt_warning }}></span>
                    </p>

                    {/* <div className={`engine_credits_cont ${exceedsCreditBalance ? "engine_credits_exceeded" : ""
                        }`}>
                        <p className="engine_credits_cont_p">
                            Estimated usage <strong>{calculatedCost.toFixed(2)}</strong> of{" "}
                            <strong>{creditBalance.toFixed(2)}</strong> credits <TbCoins />
                        </p>

                        {exceedsCreditBalance && (

                            <div className="engine_credits_warning fade-in">
                                <strong>You need a few more credits to run this simulation.</strong>
                                <p>
                                    Add credits or adjust your prompts and selected AI engines to fit your available balance.
                                </p>
                                <button className="engine_credits_warning_btn" onClick={()=> setOpenCreditsModal(true)}>
                                    Add credits
                                </button>
                            </div>

                        )}
                    </div> */}
                </>
            )}

            <ErrorMessage
                err={errorMessage}
            />

            {!loadingEngines && (
                <div className="open_frame_body_footer">
                    <button
                        type="button"
                        className="modale_save_btn fade-in"
                        onClick={navigateBack}
                    >
                        <i className="fa-solid fa-chevron-left"></i>
                        {t.back}
                    </button>

                    {canProceed && (
                        <button
                            type="button"
                            className={`modale_save_btn fade-in ${selectedEngines.length === 0
                                ? "modale_save_btn_disabled"
                                : ""
                                }`}
                            onClick={
                                saveEnginesGeo
                            }
                        >
                            {loadingSave ? (
                                <Spinner
                                    loading={true}
                                />
                            ) : (
                                <>
                                    {t.next}
                                    <i className="fa-solid fa-chevron-right"></i>
                                </>
                            )}
                        </button>
                    )}

                </div>
            )}

            <Elements stripe={stripePromise}>
                <NewCardChargeModal
                    account_id={account_id}
                    show={openCreditsModal}
                    handleClose={() => setOpenCreditsModal(false)}
                    defaultCard={""}
                    fetchData={getEngines}
                    fetchUserData={getEngines}
                />
            </Elements>


        </div>
    );
};

export default GeoEngines;
